import { initContract } from "@ts-rest/core";
import { z } from "zod";
const c = initContract();
/** Every lock failure carries a machine `code` (the web switches on it), never just a message. */
const LockError = z.object({ message: z.string(), code: z.string().optional() });
/**
 * The manual's lock state (ADR-026 Phase 1). Mirrors the legacy's 3-state `ManualLockStatus`
 * (`Unlocked` / `UserLocked` / `Locked` — `ARLMS_ManualLocking.js:11`), which the rewrite notes keep on purpose: the UI
 * decides "enter the editor" vs "show *being edited by {name}*" from exactly these three.
 */
export const ManualLockStatusSchema = z.enum(["unlocked", "held_by_me", "held_by_other"]);
/** Who holds the lock. `name` is for the "being edited by {name}" dialog — the legacy showed the holder's username. */
export const ManualLockHolderSchema = z.object({
    userId: z.number().int(),
    name: z.string().nullable(),
});
/**
 * The lock as the client sees it.
 *
 * - **`leaseId`** — the FENCING token, returned **only to the holder** (never when someone else holds it: it is an
 *   authorization secret, and leaking it would let a bystander renew or release another session's lock). Every renew /
 *   release must present it, so a lapsed holder can't act after a new one acquired (ADR-026 panel F3).
 * - **`heartbeatSeconds`** — the SERVER-advertised renew interval. The client must never hardcode one: the legacy
 *   hardcoded 110 s against a 120 s lease, a 10-second margin that lost locks on a single laptop sleep. The server sizes
 *   this at a >2× margin so a couple of missed beats are survivable.
 * - **`expiresAt`** is a dead-man's switch, NOT a session limit — an open editor renews indefinitely.
 */
export const ManualLockStateSchema = z.object({
    status: ManualLockStatusSchema,
    holder: ManualLockHolderSchema.nullable(),
    expiresAt: z.string().datetime().nullable(),
    leaseId: z.string().uuid().nullable(),
    heartbeatSeconds: z.number().int().positive(),
    /** Who is queued to take over next, if anyone (ADR-026 Phase 3b). Depth 1 — the legacy's single `requestByUser` slot. */
    waiting: ManualLockHolderSchema.nullable(),
    /**
     * When an unanswered join request auto-transfers the lock.
     *
     * This is the owner's "idle only matters when someone is waiting" rule made concrete: the clock starts on the REQUEST,
     * not on inactivity, so a holder nobody is waiting for is never rushed. Null when no one is queued.
     */
    handoverDeadline: z.string().datetime().nullable(),
});
/** A browser-session id, minted per TAB. Scoping the lease to a session (not a user) is what stops the same author's
 *  second tab co-editing and racing the body-version CAS into `#210` draft loss (ADR-026 panel F2). */
const ClientSessionIdSchema = z.string().min(8).max(128);
export const manualLockContract = c.router({
    /**
     * Read the lock state. **Strictly read-only** — the legacy's `GET /manual/:id/locked` routed into the same method as
     * acquire and DELETED expired rows (`SECURITY-FINDINGS` M5); this one never writes. Expiry is EVALUATED on read.
     */
    getManualLock: {
        method: "GET",
        path: "/manuals/:manualId/lock",
        pathParams: z.object({ manualId: z.coerce.number().int().positive() }),
        // OPTIONAL, and its absence is the SAFE default (diff-panel F1). Ownership is per SESSION, not per user, so the
        // caller must say which session is asking: without it the same author's second tab would read `held_by_me` (and be
        // handed the holder's `leaseId`) purely because the JWT `sub` matches — letting tab B enter the editor as a
        // co-holder and defeating the whole point of the lease. Omitted ⇒ never `held_by_me`, never a lease: a pure
        // "is this manual free?" probe.
        query: z.object({ clientSessionId: ClientSessionIdSchema.optional() }),
        responses: {
            200: ManualLockStateSchema,
            401: z.object({ message: z.string() }),
            403: LockError, // MANUAL_FORBIDDEN — a view-only caller has no part in the locking protocol
            404: LockError, // MANUAL_NOT_FOUND
            409: LockError, // MANUAL_IS_LINK / MANUAL_NOT_EDITABLE — un-editable ⇒ un-lockable
        },
        summary: "Read a manual's lock state (read-only; never mutates).",
    },
    /**
     * Acquire (or re-affirm) the edit lease. **It NEVER steals** — the legacy silently reassigned a live lock to the
     * caller with no ownership check (`manual.service.ts:300-309`, M5), making its check-then-lock a TOCTOU race that any
     * direct POST walked through. A live lease held by another session ⇒ **409 `MANUAL_LOCKED`** carrying the holder.
     * Re-acquiring with the SAME `clientSessionId` is idempotent and keeps the existing `leaseId` (one continuous holding
     * period — re-minting would fence the holder's own in-flight writes).
     */
    acquireManualLock: {
        method: "POST",
        path: "/manuals/:manualId/lock",
        pathParams: z.object({ manualId: z.coerce.number().int().positive() }),
        body: z.object({ clientSessionId: ClientSessionIdSchema }),
        responses: {
            200: ManualLockStateSchema,
            401: z.object({ message: z.string() }),
            403: LockError, // MANUAL_FORBIDDEN — if you can't edit it, you can't lock it
            404: LockError, // MANUAL_NOT_FOUND
            409: LockError, // MANUAL_LOCKED (another session holds a LIVE lease) / MANUAL_IS_LINK / MANUAL_NOT_EDITABLE
        },
        summary: "Acquire the manual's edit lease (409 when another session holds it — never steals).",
    },
    /** Renew the lease. Requires the matching `leaseId` AND a still-live lease: a delayed heartbeat must never
     *  RESURRECT an expired lease that another session may already hold (ADR-026 panel F3). */
    heartbeatManualLock: {
        method: "POST",
        path: "/manuals/:manualId/lock/heartbeat",
        pathParams: z.object({ manualId: z.coerce.number().int().positive() }),
        body: z.object({
            clientSessionId: ClientSessionIdSchema,
            leaseId: z.string().uuid(),
            /**
             * Is a HUMAN still working here (ADR-026, diff-panel R7-F1)?
             *
             * The heartbeat proves the TAB is alive; it says nothing about the person. That distinction decides who wins a
             * queued hand-over: an ACTIVE holder gets the prompt and decides, an ABSENT one auto-yields. Without it the
             * deadline ejected a holder mid-sentence.
             *
             * The client sets it for real interaction — keystrokes, structural edits — and **also whenever it holds unsaved
             * or failed writes**, which is how the ADR's "never auto-release while drafts are outstanding" rule is honoured
             * (the server cannot see a client-side buffer).
             */
            active: z.boolean().optional().default(false),
        }),
        responses: {
            200: ManualLockStateSchema,
            401: z.object({ message: z.string() }),
            403: LockError,
            404: LockError,
            409: LockError, // MANUAL_LOCK_LOST — expired, released, or taken over; the client must stop editing
        },
        summary: "Renew the edit lease (409 MANUAL_LOCK_LOST if it expired or changed hands).",
    },
    /** Release the lease. Holder + matching `leaseId` only. The web calls this on editor exit and via a
     *  `keepalive` unload beacon — the legacy wrote that beacon and then never called it, leaving stale locks to the TTL. */
    releaseManualLock: {
        method: "DELETE",
        path: "/manuals/:manualId/lock",
        pathParams: z.object({ manualId: z.coerce.number().int().positive() }),
        body: z.object({ clientSessionId: ClientSessionIdSchema, leaseId: z.string().uuid() }),
        responses: {
            200: ManualLockStateSchema,
            401: z.object({ message: z.string() }),
            403: LockError,
            404: LockError,
            // NOT MANUAL_LOCK_LOST: release is IDEMPOTENT — a stale lease deletes nothing and returns the current state, since
            // the web fires this from an unload beacon where an error would be noise. 409 here is only the gate's
            // MANUAL_IS_LINK / MANUAL_NOT_EDITABLE.
            409: LockError,
        },
        summary: "Release the edit lease (holder + matching leaseId only; idempotent).",
    },
    /**
     * Ask the current holder to hand the manual over (ADR-026 Phase 3b).
     *
     * Queue depth is **1**: a second waiter is refused with `MANUAL_QUEUE_TAKEN` rather than silently replacing the
     * first, which would let a latecomer jump the queue. Re-requesting from the same session is idempotent and
     * deliberately does **not** extend the deadline — otherwise a waiter could keep pushing the hand-over back.
     *
     * The holder is notified over `/cable`; if they do not respond by `handoverDeadline` the waiter's next acquire
     * simply succeeds (the deadline is evaluated lazily, like the lease's own expiry).
     */
    requestManualLock: {
        method: "POST",
        path: "/manuals/:manualId/lock/request",
        pathParams: z.object({ manualId: z.coerce.number().int().positive() }),
        body: z.object({ clientSessionId: ClientSessionIdSchema }),
        responses: {
            200: ManualLockStateSchema,
            401: z.object({ message: z.string() }),
            403: LockError, // MANUAL_FORBIDDEN — only someone who could edit it may queue for it
            404: LockError,
            409: LockError, // MANUAL_QUEUE_TAKEN (someone else is already waiting) / MANUAL_NOT_LOCKED (just acquire it) / IS_LINK / NOT_EDITABLE
        },
        summary: "Queue to take over a manual someone else is editing (depth 1).",
    },
    /**
     * Hand the lock to the queued waiter — **the holder's deliberate yes** (ADR-026 Phase 3b).
     *
     * A distinct command rather than a flag on acquire, because acquire **409s against a live foreign holder by design**
     * and release-then-acquire would open a gap a third party could win (plan-panel F4). This transfers in ONE atomic
     * CAS: same statement clears the queue, moves the holder, and mints a fresh `lease_id` — so the outgoing holder's
     * in-flight writes are fenced off the moment the hand-over commits.
     */
    handoverManualLock: {
        method: "POST",
        path: "/manuals/:manualId/lock/handover",
        pathParams: z.object({ manualId: z.coerce.number().int().positive() }),
        body: z.object({ clientSessionId: ClientSessionIdSchema, leaseId: z.string().uuid() }),
        responses: {
            200: ManualLockStateSchema,
            401: z.object({ message: z.string() }),
            403: LockError,
            404: LockError,
            409: LockError, // MANUAL_LOCK_LOST (not the holder any more) / MANUAL_NO_WAITER (nobody queued)
        },
        summary: "Hand the edit lease to the queued waiter (atomic; mints a fresh lease).",
    },
    /**
     * **Force-release another session's lock — confirm-tier only, audited.** Owner-confirmed (ADR-026): until Phase 3's
     * request-to-join queue exists, this is the ONLY escape hatch for "the holder went home with the manual locked".
     * Distinct from {@link releaseManualLock} precisely so it is separately authorized and separately audited.
     */
    forceReleaseManualLock: {
        method: "POST",
        path: "/manuals/:manualId/lock/force-release",
        pathParams: z.object({ manualId: z.coerce.number().int().positive() }),
        body: z.object({}),
        responses: {
            200: ManualLockStateSchema,
            401: z.object({ message: z.string() }),
            403: LockError, // MANUAL_FORBIDDEN — requires confirm-tier on the manual's facility
            404: LockError,
            409: LockError,
        },
        summary: "Force-release a lock held by someone else (confirm-tier, audited).",
    },
});
//# sourceMappingURL=manual-lock.contract.js.map